import { Injectable, signal, computed } from "@angular/core";

import { BehaviorSubject, Observable } from "rxjs";

import { ProviderSessionDataCreate } from "../providers/gams/provider-session.data";

export interface DecodedTokenPayload {
  uuidRGamsSession?: string;
  uuidRGumsUser?: string;
  userAccess?: string;
  iat?: number;
  exp?: number;
  sub?: string;
  uuid?: string;
  role?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  tenantId?: string;
  'x-tenant-id'?: string;
  [key: string]: any;
}

export interface UserSession extends ProviderSessionDataCreate {
  uuidRGumsUser?: string;
  userAccess?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  [key: string]: any;
}

const decodeJwtToken = (token?: string | null): DecodedTokenPayload | null => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

@Injectable({
  providedIn: 'root'
})
export class HelperSessionControllerService {
  private getInitialSession(): UserSession | null {
    try {
      const stored = window.localStorage.getItem('session');
      return stored ? JSON.parse(stored) as UserSession : null;
    } catch {
      return null;
    }
  }

  public readonly sessionSignal = signal<UserSession | null>(this.getInitialSession());
  public readonly decodedToken = computed<DecodedTokenPayload | null>(() => {
    const token = this.sessionSignal()?.token;
    return decodeJwtToken(token);
  });

  private sessionBehaviorSubject = new BehaviorSubject<UserSession | null>(this.getInitialSession());
  public sessionAsObservable: Observable<UserSession | null> = this.sessionBehaviorSubject.asObservable();

  constructor() { }

  set session(session: UserSession | null) {
    try {
      if (session) {
        window.localStorage.setItem('session', JSON.stringify(session));
      } else {
        window.localStorage.removeItem('session');
      }
    } catch (error) {
      console.warn('Failed to persist session to localStorage:', error);
    }
    this.sessionBehaviorSubject.next(session);
    this.sessionSignal.set(session);
  }

  get session(): UserSession | null {
    return this.sessionBehaviorSubject.getValue();
  }
}

